/**
 * @overview Helpers to make creation and dispatching of events a breeze!
 *
 * @module eventHelper
 */

/**
 * Creates and dispatches a DOM event
 *
 * @param {string} name
 * @param {Node} [context=document]
 * @param {boolean} [bubbles=false] Indicating if the event bubbles or not.
 * @param {boolean} [cancelable=false] Indicating if the event can be canceled or not.
 *
 * @example
 * dispatchEvent('onEvent', { context: window, bubbles: true, cancelable: true });
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Event|Event - MDN}
 */
export function dispatchEvent (name, { context = document, bubbles = false, cancelable = false } = {}) {
    const event = new Event(name, { bubbles, cancelable });
    context.dispatchEvent(event);
    return event;
}

/**
 * Creates and dispatches a custom event
 *
 * @param {string} name
 * @param {object} detail
 * @param {Node} [context=document]
 *
 * @example
 * dispatchCustomEvent('onEvent', { action: true }, window);
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/CustomEvent|CustomEvent - MDN}
 */
export function dispatchCustomEvent (name, detail, context = document) {
    const event = new CustomEvent(name, { detail });
    context.dispatchEvent(event);
    return event;
}

/**
 * Check if the key pressed in the event matches the specified variable key
 *
 * @param {KeyboardEvent} event
 * @param {keyResult} key - contains the numeric code for a particular key pressed
 * @returns {boolean}
 */
export function isKeyEvent (event, key) {
    return (event.which || event.keyCode) === key;
}

/**
 * Returns the element which dispatched the event
 *
 * @param {Event} [event=window.event]
 * @returns {boolean}
 */
export function getTarget (event = window.event) {
    return event.target || event.srcElement;
}
